<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import type { PropType } from "vue"
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { useResponsive } from "@core/composables"
import { SIDEBAR_CONTEXT_KEY } from "@core/layouts/sidebar-context"
import type { SidebarMenuItem } from "./sidebar-menu.types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            toggleExpand: "Agrandir la barre latérale",
            toggleCollapse: "Réduire la barre latérale",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = defineProps({
    model: {
        type: Array as PropType<SidebarMenuItem[]>,
        required: true,
        default: () => [],
    },
    /** Affiche des skeletons à la place du menu (ex. pendant le chargement du bâtiment). */
    loading: {
        type: Boolean,
        default: false,
    },
})

// ================================
// CONTEXTE
// ================================
const { isMobile } = useResponsive()
const sidebarContext = inject(SIDEBAR_CONTEXT_KEY)
const collapsed = computed(() => sidebarContext?.collapsed?.value ?? false)
const toggle = () => sidebarContext?.toggle?.()

/** Referme la sidebar sur mobile après sélection d’une entrée de menu. */
function onCollapseAfterNavigateIfMobile() {
    if (!isMobile.value) return
    sidebarContext?.collapse?.()
}

// ================================
// STATE
// ================================
const route = useRoute()
const router = useRouter()
const tieredMenuRef = ref<{ toggle: (event: Event) => void } | null>(null)
const tieredAnchorRef = ref<HTMLElement | null>(null)
const currentTieredModel = ref<SidebarMenuItem[]>([])
const tieredMenuTopPx = ref("0")

/** Clés des panels ouverts (pour ouvrir le sous-menu actif au chargement). */
const expandedKeys = ref<Record<string, boolean>>({})

// ================================
// COMPUTED
// ================================
const getPanelMenuItems = computed<SidebarMenuItem[]>(() => props.model)
const getRootItems = computed<SidebarMenuItem[]>(() => props.model)

// ================================
// LOGIQUE MÉTIER
// ================================
function hasSubmenu(item: SidebarMenuItem) {
    return item.items != null && item.items.length > 0
}

/**
 * PrimeVue PanelMenu peut ne transmettre qu'une partie des champs au slot header.
 * On réconcilie avec `model` pour conserver `lockMessage`, `disabled`, `route`, etc.
 */
function resolvePanelRootItem(raw: SidebarMenuItem): SidebarMenuItem {
    if (raw?.key == null) return raw
    const fromModel = props.model.find((m) => m.key === raw.key)
    return fromModel ? { ...fromModel, ...raw } : raw
}

/** True si la route courante correspond à la route de l'item (path + hash si présent). */
function isRouteMatch(itemPath: string, itemHash?: string): boolean {
    if (route.path !== itemPath) return false
    if (itemHash != null) return route.hash === itemHash
    return true
}

/** True si la route courante correspond à cet item ou à un de ses sous-items. */
function isItemActive(item: SidebarMenuItem): boolean {
    if (item.route && !hasSubmenu(item)) {
        return isRouteMatch(item.route.path, item.route.hash)
    }
    if (hasSubmenu(item) && item.items) {
        const pathMatchesParent = item.route && route.path === item.route.path
        const pathMatchesChild = item.items.some(
            (sub) => sub.route && isRouteMatch(sub.route.path, sub.route.hash)
        )
        return pathMatchesParent || pathMatchesChild
    }
    return false
}

/** True si la route courante correspond exactement à cet item (pas à un parent). */
function isExactItemActive(item: SidebarMenuItem): boolean {
    return Boolean(item.route && isRouteMatch(item.route.path, item.route.hash))
}

// ================================
// WATCHERS
// ================================
/**
 * Au chargement / changement de route : ouvrir les panels dont un enfant est actif.
 */
watch(
    () => [route.path, route.hash],
    () => {
        const keysToExpand: Record<string, boolean> = {}
        for (const item of props.model) {
            if (hasSubmenu(item) && item.key != null && isItemActive(item)) {
                keysToExpand[item.key] = true
            }
        }
        expandedKeys.value = { ...expandedKeys.value, ...keysToExpand }
    },
    { immediate: true }
)

// ================================
// INTERACTIONS
// ================================
/**
 * Navigation vers la route de l'item au clic.
 */
function onNavigateTo(item: SidebarMenuItem, event: MouseEvent) {
    event.stopPropagation()
    if (item.disabled) return
    if (item.route) {
        const loc = item.route.hash
            ? { path: item.route.path, hash: item.route.hash }
            : { path: item.route.path }
        router.push(loc)
        onCollapseAfterNavigateIfMobile()
    }
}

/**
 * Ouvre le menu tiered (sous-menu) au clic sur un item avec enfants.
 */
function onOpenTiered(event: MouseEvent, item: SidebarMenuItem) {
    if (!item.items?.length) return
    currentTieredModel.value = item.items.map((child) => ({
        ...child,
        disabled: child.disabled,
        command:
            child.route && !child.disabled
                ? () => {
                      const r = child.route!
                      const loc = r.hash
                          ? { path: r.path, hash: r.hash }
                          : { path: r.path }
                      router.push(loc)
                      onCollapseAfterNavigateIfMobile()
                  }
                : undefined,
    }))
    const anchor = tieredAnchorRef.value
    const button = event.currentTarget as HTMLElement
    if (anchor && button) {
        anchor.style.top = `${button.offsetTop}px`
        anchor.style.height = `${button.offsetHeight}px`
        const rect = anchor.getBoundingClientRect()
        tieredMenuTopPx.value = `${rect.top}px`
        tieredMenuRef.value?.toggle({
            currentTarget: anchor,
        } as unknown as Event)
    } else {
        tieredMenuRef.value?.toggle(event)
    }
}

function onTieredMenuShow() {
    const overlay = document.querySelector(
        "[data-sidebar-tiered-overlay]"
    ) as HTMLElement | null
    if (overlay && tieredMenuTopPx.value) {
        overlay.style.top = tieredMenuTopPx.value
        overlay.style.opacity = "1"
    }
}
</script>

<template>
    <div class="base-sidebar flex h-full flex-col py-4" data-sidebar-menu>
        <!-- Slot en haut du menu -->
        <slot name="top" />

        <!-- Mode étendu : PanelMenu ou skeletons -->
        <template v-if="!collapsed">
            <div class="min-h-0 flex-1 overflow-y-auto px-2">
                <template v-if="loading">
                    <div
                        v-for="i in 6"
                        :key="i"
                        class="flex items-center gap-2 py-1"
                    >
                        <BaseSkeleton width="100%" height="2.5rem" />
                    </div>
                </template>
                <BasePanelMenu
                    v-else
                    v-model:expanded-keys="expandedKeys"
                    :model="getPanelMenuItems"
                    multiple
                    :pt="{
                        root: {
                            class: 'border-none! bg-transparent!',
                        },
                        panel: {
                            class: 'border-none! bg-transparent!',
                        },
                        header: {
                            class: 'min-h-10 border-0 bg-transparent shadow-none',
                        },
                        headerContent: { class: 'min-h-10' },
                        contentContainer: {
                            class: 'border-0 bg-transparent',
                        },
                        content: { class: 'border-0 bg-transparent' },
                        rootList: {
                            class: 'border-0 bg-transparent',
                        },
                        item: { class: 'border-0 bg-transparent' },
                        itemContent: {
                            class: 'min-h-10 border-0 bg-transparent',
                        },
                    }"
                >
                    <template #item="slotProps">
                        <template
                            v-for="item in [
                                resolvePanelRootItem(slotProps.item),
                            ]"
                            :key="String(item.key ?? item.label)"
                        >
                            <RouterLink
                                v-if="
                                    item.route &&
                                    !hasSubmenu(item) &&
                                    !item.disabled
                                "
                                v-slot="{ href, navigate }"
                                :to="item.route"
                                custom
                            >
                                <a
                                    v-ripple
                                    :href="href"
                                    class="group flex min-h-10 cursor-pointer items-center px-4 text-surface-600 hover:bg-primary-50 hover:text-primary-700 rounded-sm"
                                    :class="{
                                        'bg-surface-100':
                                            isExactItemActive(item),
                                    }"
                                    @click="
                                        (e: MouseEvent) => {
                                            navigate(e)
                                            onCollapseAfterNavigateIfMobile()
                                        }
                                    "
                                >
                                    <BaseIcon
                                        v-if="item.icon"
                                        :icon="item.icon"
                                        class="text-surface-600 group-hover:text-primary"
                                    />
                                    <span :class="item.icon ? 'ml-2' : ''">{{
                                        item.label
                                    }}</span>
                                </a>
                            </RouterLink>
                            <!--
                          Entrée racine désactivée : même motif que le TieredMenu (cadenas + tooltip).
                          Les headers utilisent .p-panelmenu-header[data-p-disabled], pas .p-panelmenu-item
                          (voir style :deep ci-dessous).
                        -->
                            <span
                                v-else-if="
                                    item.route &&
                                    !hasSubmenu(item) &&
                                    item.disabled
                                "
                                class="group flex min-h-10 w-full cursor-not-allowed items-center gap-2 px-4 text-surface-600 opacity-60"
                                role="presentation"
                                aria-disabled="true"
                            >
                                <BaseIcon
                                    v-if="item.icon"
                                    :icon="item.icon"
                                    class="text-surface-600 shrink-0"
                                />
                                <span
                                    class="min-w-0 flex-1"
                                    :class="item.icon ? 'ml-2' : ''"
                                    >{{ item.label }}</span
                                >
                                <span
                                    v-if="item.lockMessage"
                                    class="sidebar-lock-trigger pointer-events-auto ml-auto inline-flex shrink-0 cursor-help rounded-sm p-0.5 -m-0.5"
                                    v-tooltip.right="{
                                        value: item.lockMessage,
                                        pt: {
                                            root: 'z-[3000]!',
                                            text: 'text-xs max-w-xs leading-normal',
                                        },
                                    }"
                                    role="img"
                                    :aria-label="item.lockMessage"
                                    tabindex="0"
                                >
                                    <i
                                        class="pi pi-lock text-xs text-surface-600"
                                        aria-hidden="true"
                                    />
                                </span>
                                <i
                                    v-else
                                    class="pi pi-lock ml-auto shrink-0 text-xs text-surface-600"
                                    aria-hidden="true"
                                />
                            </span>
                            <a
                                v-else
                                v-ripple
                                class="group flex h-10 cursor-pointer items-center px-4 text-surface-600 hover:bg-primary-50 hover:text-primary-700 rounded-sm mb-1"
                                :class="{
                                    'bg-surface-100': isItemActive(item),
                                }"
                            >
                                <BaseIcon
                                    v-if="item.icon"
                                    :icon="item.icon"
                                    class="text-surface-600 group-hover:text-primary"
                                />
                                <span :class="item.icon ? 'ml-2' : ''">{{
                                    item.label
                                }}</span>
                                <span
                                    class="pi ml-auto transition-transform"
                                    :class="
                                        item.key != null &&
                                        expandedKeys[item.key]
                                            ? 'pi-angle-down'
                                            : 'pi-angle-right'
                                    "
                                />
                            </a>
                        </template>
                    </template>
                </BasePanelMenu>
            </div>
        </template>

        <!-- Mode réduit : icônes ou skeletons + TieredMenu à droite au clic -->
        <template v-else>
            <div
                class="relative flex min-h-0 flex-1 flex-col gap-1 items-center overflow-y-auto"
            >
                <template v-if="loading">
                    <BaseSkeleton
                        v-for="i in 6"
                        :key="i"
                        shape="circle"
                        size="2.5rem"
                        class="shrink-0"
                    />
                </template>
                <template v-else>
                    <div
                        ref="tieredAnchorRef"
                        class="absolute right-0 top-0 w-0 overflow-hidden opacity-0"
                        aria-hidden="true"
                    />
                    <template
                        v-for="item in getRootItems"
                        :key="item.key ?? item.label"
                    >
                        <BaseButton
                            v-if="!hasSubmenu(item)"
                            v-tooltip.right="
                                item.disabled && item.lockMessage
                                    ? {
                                          value: item.lockMessage,
                                          pt: {
                                              root: 'z-[3000]!',
                                              text: 'text-xs max-w-xs leading-normal',
                                          },
                                      }
                                    : {
                                          value: item.label,
                                          pt: {
                                              root: 'z-[3000]!',
                                              text: 'text-xs max-w-xs leading-normal',
                                          },
                                      }
                            "
                            severity="secondary"
                            text
                            rounded
                            :disabled="
                                Boolean(item.disabled && !item.lockMessage)
                            "
                            :aria-disabled="
                                item.disabled && item.lockMessage
                                    ? true
                                    : undefined
                            "
                            :tabindex="
                                item.disabled && item.lockMessage
                                    ? -1
                                    : undefined
                            "
                            class="h-10 w-10 min-w-10 shrink-0 p-0 hover:bg-primary-50! hover:text-primary-700!"
                            :class="{
                                'bg-surface-100!': isExactItemActive(item),
                                'opacity-55!': item.disabled,
                            }"
                            :aria-label="item.label"
                            data-sidebar-toggle
                            @click="(e: MouseEvent) => onNavigateTo(item, e)"
                        >
                            <template #icon>
                                <BaseIcon v-if="item.icon" :icon="item.icon" />
                            </template>
                        </BaseButton>
                        <BaseButton
                            v-else
                            v-tooltip.right="{
                                value: item.label,
                                pt: {
                                    root: 'z-[3000]!',
                                    text: 'text-xs max-w-xs leading-normal',
                                },
                            }"
                            severity="secondary"
                            text
                            rounded
                            class="h-10 w-10 min-w-10 shrink-0 p-0 hover:bg-primary-50! hover:text-primary-700!"
                            :class="{
                                'bg-surface-100!': isExactItemActive(item),
                            }"
                            :aria-label="item.label"
                            data-sidebar-toggle
                            @click="(e: MouseEvent) => onOpenTiered(e, item)"
                        >
                            <template #icon>
                                <BaseIcon v-if="item.icon" :icon="item.icon" />
                            </template>
                        </BaseButton>
                    </template>
                </template>
            </div>
            <BaseTieredMenu
                ref="tieredMenuRef"
                :model="currentTieredModel"
                popup
                class="sidebar-tiered-menu-right"
                :pt="{
                    root: { 'data-sidebar-tiered-overlay': true },
                }"
                @show="onTieredMenuShow"
            >
                <template #item="{ item, props: itemProps }">
                    <a
                        v-ripple
                        v-bind="itemProps.action"
                        class="flex w-full items-center gap-2"
                        :class="{
                            'opacity-55': (item as SidebarMenuItem).disabled,
                        }"
                        @click="
                            !(item as SidebarMenuItem).disabled &&
                            (item as SidebarMenuItem).command?.()
                        "
                    >
                        <span class="min-w-0 flex-1">{{ item.label }}</span>
                        <!--
                          .p-tieredmenu-item.p-disabled : pas de survol sur le lien.
                          Tooltip sur le cadenas avec pointer-events-auto (voir style scoped).
                        -->
                        <span
                            v-if="
                                (item as SidebarMenuItem).disabled &&
                                (item as SidebarMenuItem).lockMessage
                            "
                            class="sidebar-lock-trigger pointer-events-auto inline-flex shrink-0 cursor-help rounded-sm p-0.5 -m-0.5"
                            v-tooltip.right="{
                                value: (item as SidebarMenuItem).lockMessage,
                                pt: {
                                    root: 'z-[3000]!',
                                    text: 'text-xs max-w-xs leading-normal',
                                },
                            }"
                            role="img"
                            :aria-label="(item as SidebarMenuItem).lockMessage"
                            tabindex="0"
                        >
                            <i
                                class="pi pi-lock shrink-0 text-xs opacity-80"
                                aria-hidden="true"
                            />
                        </span>
                        <i
                            v-else-if="(item as SidebarMenuItem).disabled"
                            class="pi pi-lock shrink-0 text-xs opacity-80"
                            aria-hidden="true"
                        />
                    </a>
                </template>
            </BaseTieredMenu>
        </template>

        <!-- Slot en bas du menu (au-dessus du bouton replier) -->
        <slot name="bottom" />

        <!-- Bouton replier / déplier -->
        <div
            class="mt-auto flex shrink-0 px-1 pt-2"
            :class="collapsed ? 'justify-center' : 'justify-end'"
            data-sidebar-toggle
        >
            <BaseButton
                v-tooltip.right="{
                    value: collapsed ? t('toggleExpand') : t('toggleCollapse'),
                    pt: {
                        root: 'z-[3000]!',
                        text: 'text-xs max-w-xs leading-normal',
                    },
                }"
                :icon="collapsed ? 'pi pi-chevron-right' : 'pi pi-chevron-left'"
                severity="secondary"
                text
                rounded
                :aria-label="
                    collapsed ? t('toggleExpand') : t('toggleCollapse')
                "
                @click="toggle"
            />
        </div>
    </div>
</template>

<style scoped>
/*
 * PrimeVue désactive les pointer-events sur les entrées en état disabled.
 * Les sous-entrées utilisent .p-panelmenu-item ; les entrées racine (ex. IRVE) sont dans
 * .p-panelmenu-header[data-p-disabled]. Rétablit le survol sur le cadenas (tooltip).
 */
:deep(.p-panelmenu-item.p-disabled .sidebar-lock-trigger),
:deep(.p-panelmenu-header[data-p-disabled] .sidebar-lock-trigger),
:deep(.p-tieredmenu-item.p-disabled .sidebar-lock-trigger) {
    pointer-events: auto;
    position: relative;
    z-index: 1;
}
</style>

<style>
/* Overlay invisible au premier rendu ; onTieredMenuShow applique top puis opacity:1 pour éviter le décalage visible. */
[data-sidebar-tiered-overlay] {
    opacity: 0;
}
</style>
